import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY_RUT = '77.112.762-2';
const COMPANY_ADDRESS = 'Anibal Pinto #340, Concepcion';
const COMPANY_EMAIL = 'finanzas@soquimat.cl';

const todayISO = () => new Date().toISOString().slice(0, 10);

const moneyCl = (value) =>
  `$ ${Number(value || 0).toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatNumber = (value, maxDigits = 2) =>
  Number(value || 0).toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  });

const loadLogoDataUrl = () =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.width || 220;
        canvas.height = image.height || 90;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }
        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = '/assets/soquimat-logo.png';
  });

const ensurePdfVerticalSpace = (doc, y, neededMm, marginBottom = 16, marginTop = 16) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + neededMm <= pageHeight - marginBottom) return y;
  doc.addPage();
  return marginTop;
};

const itemAmountClp = (item) => {
  const amount = Number(item.amount || 0);
  const currency = String(item.currency || 'CLP').trim().toUpperCase() || 'CLP';
  if (currency === 'CLP') return amount;
  const rate = Number(item.exchange_rate || 0);
  return amount * (rate > 0 ? rate : 1);
};

/**
 * Genera el PDF de una rendición de gastos (layout alineado al de cotizaciones).
 * @returns {Promise<Blob>}
 */
export async function buildRendicionPdfBlob(input = {}) {
  const items = Array.isArray(input.items) ? input.items : [];
  const reportCode = String(input.report_code || '').trim() || 'Por asignar';
  const reportDate = String(input.report_date || todayISO()).slice(0, 10);
  const reportType = String(input.report_type || 'Rendición de gastos').trim();
  const status = String(input.status || 'Pendiente').trim();
  const requesterName = String(input.requester_name || 'S/I').trim() || 'S/I';
  const requesterRut = String(input.requester_rut || 'S/I').trim() || 'S/I';
  const description = String(input.description || '').trim();
  const notes = String(input.notes || '').trim();
  const rejectionReason = String(input.rejection_reason || '').trim();
  const approvedAt = input.approved_at ? String(input.approved_at).slice(0, 10) : '';
  const approvedBy = String(input.approved_by || '').trim();

  const itemsForPdf = items.map((item) => {
    const currency = String(item.currency || 'CLP').trim().toUpperCase() || 'CLP';
    const exchangeRate = currency === 'CLP' ? 1 : Number(item.exchange_rate || 1);
    const amount = Number(item.amount || 0);
    const amountClp = itemAmountClp({ ...item, currency, exchange_rate: exchangeRate, amount });
    return {
      item_date: String(item.item_date || reportDate || '').slice(0, 10),
      category: item.category || 'Otro',
      description: item.description || '',
      document_number: item.document_number || '',
      issuer_name: item.issuer_name || '',
      issuer_rut: item.issuer_rut || '',
      payment_method: item.payment_method || '',
      titular: item.titular === 'Soquimat' ? 'Soquimat' : 'Personal',
      currency,
      exchange_rate: exchangeRate,
      amount,
      amount_clp: amountClp,
    };
  });

  const computedTotal = itemsForPdf.reduce((acc, item) => acc + Number(item.amount_clp || 0), 0);
  const computedToReturn = itemsForPdf.reduce((acc, item) => {
    if (item.titular === 'Soquimat') return acc;
    return acc + Number(item.amount_clp || 0);
  }, 0);
  const totalAmount =
    input.total_amount != null && Number.isFinite(Number(input.total_amount))
      ? Number(input.total_amount)
      : computedTotal;
  const totalToReturn =
    input.total_to_return != null && Number.isFinite(Number(input.total_to_return))
      ? Number(input.total_to_return)
      : computedToReturn;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoDataUrl = await loadLogoDataUrl();
  const headerRightX = pageWidth - 18;
  const logoTopY = 5.5;
  const headerBlockBaseY = logoTopY + 10;
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 14, logoTopY, 67.6, 31.2);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(`RENDICION N° ${reportCode}`, headerRightX, headerBlockBaseY, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Fecha: ${reportDate}`, headerRightX, headerBlockBaseY + 6, { align: 'right' });
  doc.text(`Estado: ${status}`, headerRightX, headerBlockBaseY + 11, { align: 'right' });

  const detailsTopY = 40;
  const detailsBottomY = 74;
  const leftX = 18;
  const rightX = pageWidth / 2 + 10;
  const leftDividerX = leftX - 2;
  const rightDividerX = rightX - 4;

  const clampTextToWidth = (value, maxWidth) => {
    const textValue = String(value || 'S/I');
    if (doc.getTextWidth(textValue) <= maxWidth) return textValue;
    const ellipsis = '...';
    let end = textValue.length;
    while (end > 0 && doc.getTextWidth(`${textValue.slice(0, end)}${ellipsis}`) > maxWidth) {
      end -= 1;
    }
    return `${textValue.slice(0, Math.max(0, end)).trimEnd()}${ellipsis}`;
  };

  const drawLabelValue = (x, y, label, value, maxX = pageWidth - 14) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, x, y);
    const labelWidth = doc.getTextWidth(`${label}:`);
    doc.setFont('helvetica', 'normal');
    const valueX = x + labelWidth + 1;
    const maxValueWidth = Math.max(16, maxX - valueX);
    doc.text(` ${clampTextToWidth(value, maxValueWidth)}`, valueX, y);
  };

  const drawWrappedInfoLine = (x, y, label, value, blockWidth = 118, lineHeight = 4.1) => {
    const labelText = `${label}:`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.4);
    doc.text(labelText, x, y);
    const labelWidth = doc.getTextWidth(labelText);
    const valueX = x + labelWidth + 1.2;
    const availableWidth = Math.max(20, blockWidth - (valueX - x));
    doc.setFont('helvetica', 'normal');
    const wrappedValue = doc.splitTextToSize(String(value || 'S/I'), availableWidth);
    if (wrappedValue.length > 0) doc.text(wrappedValue[0], valueX, y);
    for (let i = 1; i < wrappedValue.length; i += 1) {
      doc.text(wrappedValue[i], valueX, y + i * lineHeight);
    }
    return Math.max(1, wrappedValue.length) * lineHeight;
  };

  doc.setDrawColor(90, 90, 90);
  doc.setLineWidth(0.4);
  doc.line(leftDividerX, detailsTopY, leftDividerX, detailsBottomY);
  doc.line(rightDividerX, detailsTopY, rightDividerX, detailsBottomY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Soquimat SpA', leftX, detailsTopY + 4);
  doc.text('Solicitante', rightX, detailsTopY + 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.8);
  doc.setTextColor(130, 130, 130);
  doc.text(COMPANY_RUT, leftX, detailsTopY + 9);
  doc.text(requesterRut, rightX, detailsTopY + 9);
  doc.setTextColor(0, 0, 0);

  const leftBlockMaxX = rightDividerX - 2;
  const rightBlockMaxX = pageWidth - 14;
  doc.setFontSize(10);
  drawLabelValue(leftX, detailsTopY + 16, 'Documento', 'Rendicion de gastos', leftBlockMaxX);
  drawLabelValue(leftX, detailsTopY + 21, 'Direccion', COMPANY_ADDRESS, leftBlockMaxX);
  drawLabelValue(leftX, detailsTopY + 26, 'Email', COMPANY_EMAIL, leftBlockMaxX);
  drawLabelValue(leftX, detailsTopY + 31, 'Tipo', reportType, leftBlockMaxX);

  drawLabelValue(rightX, detailsTopY + 16, 'Nombre', requesterName, rightBlockMaxX);
  drawLabelValue(rightX, detailsTopY + 21, 'RUT', requesterRut, rightBlockMaxX);
  drawLabelValue(rightX, detailsTopY + 26, 'Estado', status, rightBlockMaxX);
  if (approvedAt) {
    drawLabelValue(
      rightX,
      detailsTopY + 31,
      'Aprobacion',
      approvedBy ? `${approvedAt} · ${approvedBy}` : approvedAt,
      rightBlockMaxX
    );
  } else {
    drawLabelValue(rightX, detailsTopY + 31, 'Items', String(itemsForPdf.length), rightBlockMaxX);
  }

  const preTableTextY = detailsBottomY + 9;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  const preTableText = description
    ? `Detalle de gastos rendidos (${itemsForPdf.length} item${itemsForPdf.length === 1 ? '' : 's'}). Motivo: ${description}`
    : `Detalle de gastos rendidos (${itemsForPdf.length} item${itemsForPdf.length === 1 ? '' : 's'}) segun detalle a continuacion:`;
  const preTableLines = doc.splitTextToSize(preTableText, pageWidth - 28);
  doc.text(preTableLines, 14, preTableTextY, { lineHeightFactor: 1.45 });

  const itemRows = itemsForPdf.map((item, idx) => [
    idx + 1,
    item.item_date || '—',
    item.category || '—',
    item.description || '—',
    item.document_number || '—',
    item.titular || 'Personal',
    item.currency || 'CLP',
    formatNumber(item.amount, 2),
    item.currency === 'CLP' ? '1' : formatNumber(item.exchange_rate, 4),
    moneyCl(item.amount_clp),
  ]);

  autoTable(doc, {
    startY: preTableTextY + preTableLines.length * 4.6 + 3,
    head: [['#', 'Fecha', 'Cat.', 'Descripcion', 'Doc.', 'Titular', 'Mon.', 'Monto', 'TC', 'CLP']],
    body: itemRows.length
      ? itemRows
      : [['—', '—', '—', 'Sin items', '—', '—', '—', '—', '—', '—']],
    showHead: 'everyPage',
    styles: { fontSize: 6.2, cellPadding: 1.1, halign: 'center', overflow: 'linebreak' },
    headStyles: {
      fillColor: [20, 30, 44],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 6.5,
      cellPadding: 1.2,
    },
    bodyStyles: { fontSize: 6.2, cellPadding: 1.1 },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 18, halign: 'center' },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 'auto', halign: 'left' },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 16, halign: 'center' },
      6: { cellWidth: 12, halign: 'center' },
      7: { cellWidth: 18, halign: 'right' },
      8: { cellWidth: 14, halign: 'right' },
      9: { cellWidth: 22, halign: 'right' },
    },
    margin: { left: 14, right: 14, bottom: 16 },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) {
        data.cell.styles.halign = 'left';
      }
    },
  });

  const summaryBlockNeeded = 34;
  let blockTop = (doc.lastAutoTable?.finalY || detailsBottomY + 40) + 6;
  blockTop = ensurePdfVerticalSpace(doc, blockTop, summaryBlockNeeded);

  const totalsColWidth = 72;
  const totalsColX = pageWidth - 14 - totalsColWidth;
  const infoBlockWidth = Math.max(70, totalsColX - 14 - 8);
  const infoStep = 4.2;

  autoTable(doc, {
    startY: blockTop,
    margin: { left: totalsColX, right: 14 },
    tableWidth: totalsColWidth,
    body: [
      [
        { content: 'TOTAL RENDICION', styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] } },
        {
          content: moneyCl(totalAmount),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] },
        },
      ],
      [
        { content: 'TOTAL A DEVOLVER', styles: { halign: 'right', fontStyle: 'bold', fillColor: [235, 235, 235] } },
        {
          content: moneyCl(totalToReturn),
          styles: { halign: 'right', fontStyle: 'bold', fillColor: [235, 235, 235] },
        },
      ],
    ],
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 1.6 },
    columnStyles: {
      0: { cellWidth: 42 },
      1: { cellWidth: 30 },
    },
  });

  let infoY = blockTop + 1.5;
  infoY += drawWrappedInfoLine(14, infoY, 'Tipo', reportType, infoBlockWidth, infoStep);
  infoY += drawWrappedInfoLine(14, infoY, 'Solicitante', requesterName, infoBlockWidth, infoStep);
  infoY += drawWrappedInfoLine(14, infoY, 'RUT', requesterRut, infoBlockWidth, infoStep);
  infoY += drawWrappedInfoLine(
    14,
    infoY,
    'Items',
    `${itemsForPdf.length} · Personal ${moneyCl(totalToReturn)} · Soquimat ${moneyCl(totalAmount - totalToReturn)}`,
    infoBlockWidth,
    infoStep
  );

  const notesLines = [];
  if (notes) notesLines.push(...notes.split(/\n|;/).map((l) => l.trim()).filter(Boolean));
  if (rejectionReason) notesLines.push(`Motivo rechazo: ${rejectionReason}`);

  let afterSummaryY = Math.max(doc.lastAutoTable?.finalY || blockTop + 20, infoY + 2) + 6;
  afterSummaryY = ensurePdfVerticalSpace(doc, afterSummaryY, notesLines.length ? 28 : 18);

  if (notesLines.length) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Notas / observaciones', 14, afterSummaryY);
    afterSummaryY += 3;
    const boxTop = afterSummaryY;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const wrapped = notesLines.flatMap((line, idx) =>
      doc.splitTextToSize(`${idx + 1}. ${line}`, pageWidth - 36)
    );
    const boxHeight = Math.max(14, wrapped.length * 4 + 6);
    afterSummaryY = ensurePdfVerticalSpace(doc, afterSummaryY, boxHeight + 4);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.rect(14, afterSummaryY, pageWidth - 28, boxHeight);
    let textY = afterSummaryY + 5;
    wrapped.forEach((line) => {
      doc.text(line, 18, textY);
      textY += 4;
    });
    afterSummaryY += boxHeight + 8;
  }

  afterSummaryY = ensurePdfVerticalSpace(doc, afterSummaryY, 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  doc.text(
    'Documento interno Soquimat SpA · Los montos en moneda extranjera se convierten a CLP con el tipo de cambio indicado.',
    14,
    afterSummaryY
  );
  doc.setTextColor(0, 0, 0);

  return doc.output('blob');
}

export async function buildRendicionPdfObjectUrl(input = {}) {
  const blob = await buildRendicionPdfBlob(input);
  return URL.createObjectURL(blob);
}

export async function downloadRendicionPdf(input = {}, filename) {
  const blob = await buildRendicionPdfBlob(input);
  const code = String(input.report_code || 'rendicion').trim() || 'rendicion';
  const name = filename || `${code}.pdf`;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return blob;
}
